import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import "./weight-slider.scss";
import Button from "../button/button";
import { DownArrowCardIcon, UpArrowCardIcon } from "../../../assets/Icons";

const TICK_SPACING = 14; // px per unit

const WEIGHT_HINTS: { max: number; label: string }[] = [
  { max: 2,   label: "Similar to a paperclip" },
  { max: 4,   label: "Similar to a coin" },
  { max: 5,   label: "Similar to a AA battery" },
  { max: 6,   label: "Similar to a standard pencil" },
  { max: 8,   label: "Similar to a house key" },
  { max: 9,   label: "Similar to a lip balm tube" },
  { max: 10,  label: "Similar to a small key" },
  { max: 11,  label: "Similar to a large paperclip" },
  { max: 15,  label: "Similar to a spoon" },
  { max: 17,  label: "Similar to a USB flash drive" },
  { max: 20,  label: "Similar to a golf ball" },
  { max: 22,  label: "Similar to a AAA battery" },
  { max: 24,  label: "Similar to a large coin (half dollar)" },
  { max: 25,  label: "Similar to a battery pack" },
  { max: 29,  label: "Similar to a slice of bread" },
  { max: 30,  label: "Similar to a small pack of sugar" },
  { max: 33,  label: "Similar to a poker chip" },
  { max: 36,  label: "Similar to a matchbox (full)" },
  { max: 39,  label: "Similar to a set of earbuds (single side)" },
  { max: 50,  label: "Similar to a TV remote" },
  { max: 55,  label: "Similar to a standard egg" },
  { max: 59,  label: "Similar to a tennis ball" },
  { max: 80,  label: "Similar to a smartphone" },
  { max: 85,  label: "Similar to a deck of cards" },
  { max: 90,  label: "Similar to a small padlock" },
  { max: 99,  label: "Similar to a baseball" },
  { max: 100, label: "Similar to a large bar of soap" },
  { max: 110, label: "Similar to a medium apple" },
  { max: 120, label: "Similar to a billiard ball" },
  { max: 130, label: "Similar to a hockey puck" },
  { max: 140, label: "Similar to a standard light bulb" },
  { max: 149, label: "Similar to a small stapler" },
  { max: 150, label: "Similar to a small tablet" },
  { max: 160, label: "Similar to a stick of deodorant" },
  { max: 170, label: "Similar to a hardcover pocket book" },
  { max: 179, label: "Similar to a large smartphone (Pro model)" },
  { max: 180, label: "Similar to a computer mouse" },
  { max: 190, label: "Similar to a full tube of toothpaste" },
  { max: 199, label: "Similar to a baseball glove (palm only)" },
  { max: 200, label: "Similar to a coffee mug" },
  { max: 215, label: "Similar to a small candle jar" },
  { max: 230, label: "Similar to a can of soda (half full)" },
  { max: 249, label: "Similar to a large orange" },
  { max: 250, label: "Similar to a standard stick of butter (2 sticks)" },
  { max: 265, label: "Similar to a full can of soup" },
  { max: 280, label: "Similar to a small water bottle (half full)" },
  { max: 299, label: "Similar to a full can of soda" },
  { max: 300, label: "Similar to a laptop charger" },
];

const getWeightHint = (value: number): string =>
  WEIGHT_HINTS.find((h) => value <= h.max)?.label ?? "";

type WeightSliderProps = {
  initialValue?: number;
  min?: number;
  max?: number;
  unit?: string;
  onContinue: (value: number) => void;
};

const WeightSlider: React.FC<WeightSliderProps> = ({
  initialValue = 1,
  min = 1,
  max = 300,
  unit = "grams",
  onContinue,
}) => {
  // clamp drag to max (300); user can type above max via input
  const clampVisual = useCallback(
    (n: number) => Math.max(min, Math.min(max, n)),
    [min, max]
  );

  // value drives the slider ruler (clamped to max); inputText is what user sees/types (uncapped above max)
  const [value, setValue] = useState<number>(clampVisual(initialValue));
  const [inputText, setInputText] = useState<string>(String(Math.max(min, initialValue)));
  const [helpOpen, setHelpOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(300);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setContainerWidth(el.clientWidth);
    const obs = new ResizeObserver((entries) => {
      setContainerWidth(entries[0].contentRect.width);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const indicatorX = containerWidth / 2;

  const dragStartX = useRef<number | null>(null);
  const dragStartValue = useRef<number>(value);

  // Tracks the parsed input number synchronously — updated in every handler that
  // changes the input, so slider handlers always see the true current value even
  // if React hasn't committed the latest render yet (stale-closure prevention).
  const liveInputValueRef = useRef<number>(Math.max(min, clampVisual(initialValue)));

  // ── Slider drag + click handlers ─────────────────────────────
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    const snapped = clampVisual(Math.round(value + (x - indicatorX) / TICK_SPACING));
    // Guard uses the synchronously-maintained ref, not the render-cycle closure.
    if (liveInputValueRef.current > max && snapped >= max) return;
    liveInputValueRef.current = snapped;
    setValue(snapped);
    setInputText(String(snapped));

    dragStartX.current = e.clientX;
    dragStartValue.current = snapped;
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragStartX.current === null) return;
    const delta = dragStartX.current - e.clientX;
    const next = clampVisual(Math.round(dragStartValue.current + delta / TICK_SPACING));
    if (liveInputValueRef.current > max && next >= max) return;
    if (next !== value) {
      liveInputValueRef.current = next;
      setValue(next);
      setInputText(String(next));
    }
  };

  const handlePointerUp = () => {
    dragStartX.current = null;
  };

  // Issue 3: keyboard support for the ruler
  const handleRulerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      if (liveInputValueRef.current > max) return; // block increase when above max
      e.preventDefault();
      const next = clampVisual(value + 1);
      setValue(next);
      setInputText(String(next));
    } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      e.preventDefault();
      const next = clampVisual(value - 1);
      setValue(next);
      setInputText(String(next));
    }
  };

  // ── Input box handlers ────────────────────────────────────────
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Allow empty string so user can clear and retype
    if (raw === "" || raw === "-") {
      setInputText(raw);
      return;
    }
    const parsed = parseInt(raw, 10);
    if (isNaN(parsed)) return; // reject non-numeric
    liveInputValueRef.current = parsed; // sync ref immediately, before React re-renders
    setInputText(raw);
    // Issue 1: allow values > max — only clamp for visual slider position
    setValue(clampVisual(parsed));
  };

  const handleInputBlur = () => {
    const parsed = parseInt(inputText, 10);
    if (isNaN(parsed) || inputText.trim() === "") {
      // Empty or non-numeric — reset to min so slider isn't in a broken state
      liveInputValueRef.current = min;
      setValue(min);
      setInputText(String(min));
    } else {
      // Keep the typed value as-is (even if < min like "0") so button stays disabled
      // and user can see and fix the invalid value — don't silently change it
      liveInputValueRef.current = parsed;
      setInputText(String(parsed));
      setValue(clampVisual(parsed));
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      (e.currentTarget as HTMLInputElement).blur();
      return;
    }
    // Issue 4: allow system shortcuts (paste, copy, select-all, cut)
    if (e.ctrlKey || e.metaKey) return;
    // Allow: digits, backspace, delete, arrows, tab, home, end
    const allowed = [
      "Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End",
    ];
    if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  // Issue 4: handle paste explicitly
  const handleInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text");
    const parsed = parseInt(text, 10);
    if (!isNaN(parsed) && parsed >= min) {
      liveInputValueRef.current = parsed;
      setInputText(String(parsed));
      setValue(clampVisual(parsed));
    }
  };

  // ── Tick marks ───────────────────────────────────────────────
  const ticks = useMemo(() => {
    const result: { gram: number; type: "minor" | "major5" | "major10" }[] = [];
    for (let i = min; i <= max; i++) {
      const type =
        i % 10 === 0 ? "major10" : i % 5 === 0 ? "major5" : "minor";
      result.push({ gram: i, type });
    }
    return result;
  }, [min, max]);

  // Translate ruler so tick at current value aligns with center indicator
  const translateX = indicatorX - (value - min) * TICK_SPACING;

  // Derive validity from raw parsed input — Math.max must not be used here
  // as it would mask invalid values like 0 by silently returning min(1)
  const parsedInput = parseInt(inputText, 10);
  const isValidInput = !isNaN(parsedInput) && parsedInput >= min;
  const numericValue = isValidInput ? parsedInput : min; // pass raw value — no max cap
  const isValidForContinue = isValidInput;

  return (
    <div className="weight-slider">
      {/* Editable value box */}
      <div className="weight-slider__value-box">
        <input
          className="weight-slider__value-input"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={inputText}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          onPaste={handleInputPaste}
          maxLength={3}
          aria-label={`Weight in ${unit}`}
        />
      </div>
      <div className="weight-slider__unit">{unit}</div>

      {/* Draggable ruler — Issue 2: click-to-position, Issue 3: keyboard */}
      <div
        ref={containerRef}
        className="weight-slider__ruler-container"
        tabIndex={0}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onKeyDown={handleRulerKeyDown}
        aria-label={`Weight slider, ${value} ${unit}`}
        role="slider"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
      >
        <div className="weight-slider__indicator" />
        <div
          className="weight-slider__ruler"
          style={{ transform: `translateX(${translateX}px)` }}
        >
          {ticks.map(({ gram, type }) => (
            <div
              key={gram}
              className={`weight-slider__tick weight-slider__tick--${type}`}
            />
          ))}
        </div>
      </div>

      {/* Hint text — only for valid values within slider range */}
      {isValidInput && parsedInput <= max && (
        <div className="weight-slider__hint">{getWeightHint(numericValue)}</div>
      )}

      {/* Need some help? accordion */}
      <div className={`weight-slider__help${helpOpen ? " open" : ""}`}>
        <button
          type="button"
          className="weight-slider__help-header"
          onClick={() => setHelpOpen((p) => !p)}
          aria-expanded={helpOpen}
        >
          <span>Need some help?</span>
          {helpOpen ? <UpArrowCardIcon /> : <DownArrowCardIcon />}
        </button>
        {helpOpen && (
          <div className="weight-slider__help-content">
            <p>
              If you have a kitchen scale, you can use that to weigh your item.
              Don't have one? Try comparing it to something familiar:
            </p>
            <p>
              A <strong>U.S. quarter</strong> weighs about{" "}
              <strong>6 grams</strong>.
            </p>
            <p>
              A <strong>paperclip</strong> weighs about{" "}
              <strong>1 gram</strong>.
            </p>
            <p>
              A <strong>standard ring</strong> usually weighs between{" "}
              <strong>2–6 grams</strong>.
            </p>
          </div>
        )}
      </div>

      {/* Continue button — Issue 5: disabled when input is invalid */}
      <div className="continue-button">
        <Button
          label="Continue"
          onClick={() => onContinue(numericValue)}
          styleType="primary"
          disabled={!isValidForContinue}
        />
      </div>
    </div>
  );
};

export default WeightSlider;
